import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../shadComponents/ui/dialog';
import { Button } from '../shadComponents/ui/button';
import { Input } from '../shadComponents/ui/input';
import { Label } from '../shadComponents/ui/label';
import { Switch } from '../shadComponents/ui/switch';
import useAuthenticatedApi from '../Authentication/components/hooks/useAuthenticatedApi';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onUserCreated: () => void;
}

const GalleryUserCreateModal: React.FC<Props> = ({
    open,
    onOpenChange,
    onUserCreated,
}) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        igHandle: '',
        facialRecognitionOptedIn: false,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const authenticatedApi = useAuthenticatedApi();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const response = await authenticatedApi.post('/gallery-users', {
                ...formData,
            });

            if (response.status !== 200) {
                throw new Error('Failed to create user');
            }

            onUserCreated();
            onOpenChange(false);
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                igHandle: '',
                facialRecognitionOptedIn: false,
            });
        } catch (error) {
            console.error('Error creating user:', error);
            // TODO: Add proper error handling/notification
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Create New Gallery User</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="first_name">First Name</Label>
                            <Input
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="last_name">Last Name</Label>
                            <Input
                                id="lastName"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="phone">Phone</Label>
                            <Input
                                id="phone"
                                name="phone"
                                type="tel"
                                value={formData.phone}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="ig_handle">Instagram Handle</Label>
                        <Input
                            id="ig_handle"
                            name="igHandle"
                            value={formData.igHandle}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="flex items-center space-x-2">
                        <Switch
                            id="facialRecognitionOptedIn"
                            checked={formData.facialRecognitionOptedIn}
                            onCheckedChange={(checked: boolean) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    facial_recognition_opted_in: checked,
                                }))
                            }
                        />
                        <Label htmlFor="facial_recognition">
                            Enable Facial Recognition
                        </Label>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Creating...' : 'Create User'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default GalleryUserCreateModal;
